import { isEmpty, compact, remove } from 'lodash';
import { Brief, BriefSection, DynamicSection, SectionTypes } from '@fiverr-private/ai_brief';
import { Gql } from '@fiverr-private/dynamic_matching';
import {
    GENERATED_CONTENT_EXCLUDED_SECTION_IDS,
    MANDATORY_GENERATED_SECTION_TITLES,
    MetadataSectionContent,
    ScreeningQuestionsContent,
    screeningQuestionsSectionID,
} from '../components';
import { idealCandidateSectionID } from '../components/Sections/IdealCandidate';
import { IdealCandidatePreferences } from '../components/Sections/IdealCandidate/utils/types';
import { snakeCaseFreelancerPreferences } from '../components/Sections/IdealCandidate/utils/utils';
import {
    BudgetAndTimelineContent,
    mapTimeUnitsToApiTimeUnit,
} from '../components/Sections/BudgetAndTimeline/utils/types';
import { BUDGET_AND_TIMELINE_SECTION_ID } from '../components/Sections/BudgetAndTimeline';
import { NEED_TYPE_SECTION_ID, BriefNeedType } from '../components/Sections/NeedType';
import { getCentsInCurrency } from '../components/Sections/Budget/formatCurrency';
import { Currency } from '../types/currency';
import { NOT_SURE_METADATA_KEY } from '../constants';
import { EditBriefRequestData } from './editBrief';
import { MetadataAnswersType, ScreeningQuestion } from './submitBrief';

const getMetadataAnswers = (sections: BriefSection[]): Record<string, string[]> => {
    const metadataAnswersSections = sections.filter(
        (section) =>
            section.type === SectionTypes.metadata && section.included && section.content[0] !== NOT_SURE_METADATA_KEY
    );

    return metadataAnswersSections.reduce((acc: Record<string, string[]>, section) => {
        const metadataContent: MetadataSectionContent = (section as any).metadata;
        if (metadataContent?.question?.metadata_type) {
            acc[metadataContent.question.metadata_type] = section.content as unknown as string[];
        }
        return acc;
    }, {});
};

const getScreeningQuestions = (sections: BriefSection<ScreeningQuestionsContent>[]): ScreeningQuestion[] | null => {
    const screeningQuestionsSection = sections.find(
        (section) => section.id === screeningQuestionsSectionID && section.included
    );

    if (!screeningQuestionsSection) {
        return null;
    }

    return screeningQuestionsSection.content.questions.map((question) => ({
        kind: question.suggestedQuestionId
            ? Gql.DmBriefScreeningQuestionKind.Suggested
            : Gql.DmBriefScreeningQuestionKind.Custom,
        question: question.text,
    }));
};

const getNeedTypeData = (brief: Brief<any>) => {
    const needTypeSection = brief.sections.find((section) => section.id === NEED_TYPE_SECTION_ID && section.included);
    const needTypeContent = (needTypeSection?.content ?? {}) as BriefNeedType;
    const { needType } = needTypeContent;

    const defaultEnrichment = {
        need_type: needType,
    };

    if (needType !== Gql.DmBriefNeedType.OneTime) {
        return defaultEnrichment;
    }

    const sectionWithPotential = brief.sections.find(
        (section) => section.id === BUDGET_AND_TIMELINE_SECTION_ID && section.included
    );

    return {
        ...defaultEnrichment,
        need_type_details: {
            ongoing_work_potential: Boolean(sectionWithPotential?.content?.ongoingWorkPotential),
        },
    };
};

const extractBudgetAndTimeline = (brief: Brief, currency: Currency): Partial<EditBriefRequestData> => {
    const budgetAndTimelineSection = brief.sections.find(
        (section) => section.id === BUDGET_AND_TIMELINE_SECTION_ID
    ) as DynamicSection;

    const budgetAndTimelineContent = budgetAndTimelineSection?.content as BudgetAndTimelineContent;

    if (!budgetAndTimelineContent) {
        return {};
    }

    const { budgetInCents, hourlyBudgetRange, timelineDetails, deliveryDate, pricingType, timeline } =
        budgetAndTimelineContent;

    const isHourly = pricingType === Gql.BriefPricingType.Hourly;
    const isFixed = pricingType === Gql.BriefPricingType.Fixed;

    const hourlyMin = hourlyBudgetRange?.min;
    const hourlyMax = hourlyBudgetRange?.max;

    let maxBudgetForRange: number | undefined;

    if (isHourly) {
        maxBudgetForRange = hourlyMax;
    } else if (isFixed) {
        maxBudgetForRange = budgetInCents;
    }
    return {
        ...(isFixed && budgetInCents
            ? {
                  budget_in_cents: budgetInCents,
                  original_buyer_budget: {
                      amount: getCentsInCurrency(budgetInCents, currency) * 100,
                      currency: currency.name,
                  },
              }
            : {}),

        ...(isHourly && hourlyMin && hourlyMax
            ? {
                  budget_range_in_cents: {
                      min: hourlyMin,
                      max: hourlyMax,
                  },
              }
            : {}),

        ...(maxBudgetForRange
            ? {
                  original_buyer_budget_range: {
                      max: {
                          amount: getCentsInCurrency(maxBudgetForRange, currency),
                          currency: currency.name,
                      },
                      ...(isHourly && hourlyMin
                          ? {
                                min: {
                                    amount: getCentsInCurrency(hourlyMin, currency),
                                    currency: currency.name,
                                },
                            }
                          : {}),
                  },
              }
            : {}),

        delivery_date_epoch_ms: deliveryDate,
        pricing_type: pricingType,
        timeline,

        ...(!isEmpty(timelineDetails)
            ? {
                  timeline_details: {
                      ...(!isEmpty(timelineDetails.generalTimeline)
                          ? {
                                general_timeline: {
                                    ...(timelineDetails.generalTimeline.unit && {
                                        unit: mapTimeUnitsToApiTimeUnit(timelineDetails.generalTimeline.unit),
                                    }),
                                    ...(timelineDetails.generalTimeline.value && {
                                        value: timelineDetails.generalTimeline.value,
                                    }),
                                },
                            }
                          : {}),
                      ...(!isEmpty(timelineDetails.timeCommitment)
                          ? { time_commitment: timelineDetails.timeCommitment?.toUpperCase() }
                          : {}),
                      ...(!isEmpty(timelineDetails.hiringDuration)
                          ? { hiring_duration: timelineDetails.hiringDuration?.toUpperCase() }
                          : {}),
                      ...(!isEmpty(timelineDetails.deliveriesFrequency)
                          ? {
                                deliveries_frequency: {
                                    ...(timelineDetails.deliveriesFrequency.unit && {
                                        unit: mapTimeUnitsToApiTimeUnit(timelineDetails.deliveriesFrequency.unit),
                                    }),
                                    ...(timelineDetails.deliveriesFrequency.value && {
                                        value: timelineDetails.deliveriesFrequency.value,
                                    }),
                                },
                            }
                          : {}),
                  },
              }
            : {}),
    };
};

export const mapBriefToEditRequestData = (
    brief: Brief & {
        categoryId?: number;
        subCategoryId?: number;
        nestedSubCategoryId?: number | null;
        description?: string;
    },
    currency: Currency
): EditBriefRequestData => {
    const briefSections = (brief.sections as DynamicSection[]).filter(
        (section) => !isEmpty(section.content) && section.included
    );

    const budgetAndTimeline = extractBudgetAndTimeline(brief, currency);
    const metadataAnswers = getMetadataAnswers(briefSections);

    // Category fields from the brief
    const categoryFields = {
        ...(brief.categoryId ? { category_id: brief.categoryId } : {}),
        ...(brief.subCategoryId ? { sub_category_id: brief.subCategoryId } : {}),
        nested_sub_category_id: brief.nestedSubCategoryId ?? null,
    };

    const [idealCandidateSection] = remove(briefSections, (section) => section.id === idealCandidateSectionID);
    const { skillsAndExperience, ...freelancerPreferences } =
        idealCandidateSection?.content ?? ({} as IdealCandidatePreferences);

    const nonMandatorySections = briefSections.filter(
        (section) =>
            !section.mandatory &&
            section.included &&
            section.type !== SectionTypes.metadata &&
            !GENERATED_CONTENT_EXCLUDED_SECTION_IDS.includes(section.id)
    );

    if (skillsAndExperience) {
        nonMandatorySections.push({
            title: 'Ideal Candidate',
            content: skillsAndExperience,
            included: true,
        } as DynamicSection);
    }

    const mandatoryGeneratedSections = briefSections.filter(
        (section) =>
            section.title &&
            MANDATORY_GENERATED_SECTION_TITLES.includes(section.title) &&
            section.type === SectionTypes.generated
    );

    const attachmentSection = brief.sections.find((section) => section.type === SectionTypes.attachments);
    const titleSection = brief.sections.find((section) => section.id === 'title');
    const title = titleSection?.content || brief.title;

    const screeningQuestions = getScreeningQuestions(briefSections);

    const briefRequestData: EditBriefRequestData = {
        ...categoryFields,
        metadata_answers: {
            service_options: metadataAnswers,
            ...(!isEmpty(freelancerPreferences)
                ? { freelancer_preferences: snakeCaseFreelancerPreferences(freelancerPreferences) }
                : {}),
        } as MetadataAnswersType,
        title,
        ...(brief.description ? { description: brief.description } : {}),
        content: {
            generated_content: [...mandatoryGeneratedSections, ...nonMandatorySections].map(
                ({ id, title, content }) => ({
                    id,
                    title,
                    content,
                })
            ),
        },
        ...budgetAndTimeline,
        attachment_ids: attachmentSection?.included
            ? compact((attachmentSection?.attachments || []).map((attachment) => attachment.pending_attachment_id))
            : [],
        ...getNeedTypeData(brief),
        ...(screeningQuestions ? { screening_questions: screeningQuestions } : {}),
    };

    return briefRequestData;
};
